import { useEffect } from 'react';
import { actionType, showActionType } from 'config/constants';
import {
  Checkbox,
  DialogContentText,
  FormControl,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography,
} from '@material-ui/core';
import { useFormik } from 'formik';
import { usePreferencesFunctions } from 'contexts/PreferencesContext';
import * as yup from 'yup';
import clsx from 'clsx';
import DefaultModal from 'components/DefaultModal';
import useStyles from './styles';

const selectActionValidationSchema = yup
  .object({
    selectedAction: yup
      .string()
      .oneOf(['DEACTIVATE', 'DELETE'], 'Veuillez choisir une valeur')
      .required('Veuillez choisir une valeur'),
  })
  .defined();

const transferValidationSchema = yup
  .object({
    transfer: yup
      .string()
      .oneOf(['TRANSFER', 'NO_TRANSFER'], 'Veuillez choisir une valeur')
      .required('Veuillez choisir une valeur'),
  })
  .defined();

const skipWarningValidationSchema = yup
  .object({
    skipWarning: yup.bool(),
  })
  .defined();

const texts = {
  deactivate: {
    title: 'Désactivation d’un membre de l’équipe',
    subTitle: "La désactivation d'un moniteur entraine celle de son agenda",
  },
  delete: {
    title: 'Suppression d’un membre de l’équipe',
    subTitle: "La suppression d'un moniteur entraine celle de son agenda",
  },
};

const getTitle = (modalType, showAction) => {
  switch (showAction) {
    case showActionType.CONFLICT_SELECT:
      return 'Attention';
    case showActionType.SELECT_ACTION:
      return 'Que voulez-vous faire des prestations liées à ce moniteur ?';
    default:
      return texts[modalType].title;
  }
};

const getValidationSchema = (showAction) => {
  switch (showAction) {
    case showActionType.SHOULD_TRANSFER:
      return transferValidationSchema;
    case showActionType.SELECT_ACTION:
      return selectActionValidationSchema;
    default:
      return skipWarningValidationSchema;
  }
};

function ActionOnEmployeeModal({ onClose, loading, onConfirm, showAction, modalType }) {
  const classes = useStyles();
  const { changePreference } = usePreferencesFunctions();

  const { handleChange, handleSubmit, resetForm, values } = useFormik({
    initialValues: {
      selectedAction: '',
      skipWarning: false,
      transfer: '',
    },
    validationSchema: getValidationSchema(showAction),
    onSubmit: (values) => {
      if (values.skipWarning) {
        changePreference(`show_${modalType}_employee_warning`, false);
      }
      onConfirm?.(values);
    },
  });

  useEffect(() => {
    resetForm();
  }, [resetForm, showAction]);

  const handleCancelConflictDeleteOrDeactivate = () => {
    onConfirm({ forceAction: false });
  };

  const handleConflictDeleteOrDeactivateSubmit = () => {
    onConfirm({ forceAction: true });
  };

  const ConflictSelectAction = () => (
    <Grid container item xs={12}>
      <DialogContentText className={clsx(upperTextClasses, classes.primaryText, classes.modalText)}>
        {`Certaines prestations ont déjà été réservées, les activités concernées seront bloquées${
          modalType === actionType.DELETE ? ' et ce moniteur ne sera pas supprimé' : ''
        }.`}
      </DialogContentText>
    </Grid>
  );

  const SelectActionForm = () => (
    <>
      <FormControl className={classes.formControl} component="fieldset">
        <RadioGroup
          aria-label="selectedAction"
          className={classes.radioGroup}
          name="selectedAction"
          value={values.selectedAction}
          onChange={handleChange}
        >
          <Box className={classes.radio}>
            <Radio color="primary" value="DEACTIVATE" />
            <Typography>Désactiver les prestations</Typography>
          </Box>
          <Box className={classes.radio}>
            <Radio color="primary" value="DELETE" />
            <Typography>Supprimer les prestations</Typography>
          </Box>
        </RadioGroup>
      </FormControl>
    </>
  );

  const TransferForm = () => (
    <>
      <Grid container item xs={12} justify="center">
        <DialogContentText className={clsx(upperTextClasses, classes.primaryText, classes.modalText)}>
          {texts[modalType].subTitle}
        </DialogContentText>
      </Grid>

      <DialogContentText className={clsx(classes.modalText, classes.secondaryText)}>
        Voulez vous transférer son agenda vers un autre moniteur ?
      </DialogContentText>
      <FormControl className={classes.formControl} component="fieldset">
        <RadioGroup
          aria-label="transfer"
          className={classes.radioGroup}
          name="transfer"
          value={values.transfer}
          onChange={handleChange}
        >
          <FormControlLabel
            className={classes.radio}
            value="TRANSFER"
            control={<Radio color="primary" />}
            label="Oui"
          />
          <FormControlLabel
            className={classes.radio}
            value="NO_TRANSFER"
            control={<Radio color="primary" />}
            label="Non"
          />
        </RadioGroup>
      </FormControl>
    </>
  );

  const WarningForm = () => (
    <>
      <Grid container item xs={12} justify="center">
        <DialogContentText className={clsx(upperTextClasses, classes.primaryText, classes.modalText)}>
          {texts[modalType].subTitle}
        </DialogContentText>
      </Grid>

      <FormControlLabel
        className={classes.preferenceCheckbox}
        name="skipWarning"
        control={<Checkbox color="primary" name="skipWarning" checked={values.skipWarning} onChange={handleChange} />}
        label="Ne plus afficher cet avertissement"
      />
    </>
  );

  let upperTextClasses = classes.biggerModalText;
  if (showAction === showActionType.SHOULD_TRANSFER) upperTextClasses = classes.lesserModalText;

  const close = showAction === showActionType.CONFLICT_SELECT ? handleCancelConflictDeleteOrDeactivate : onClose;
  const title = getTitle(modalType, showAction);
  const submit = showAction === showActionType.CONFLICT_SELECT ? handleConflictDeleteOrDeactivateSubmit : handleSubmit;
  return (
    <DefaultModal
      forceConfirm
      loading={loading}
      open={!!showAction}
      title={title}
      onClose={close}
      onConfirm={submit}
      square
    >
      {showAction === showActionType.CONFLICT_SELECT && <ConflictSelectAction />}
      {showAction === showActionType.PREFERENCE_WARNING && <WarningForm />}
      {showAction === showActionType.SELECT_ACTION && <SelectActionForm />}
      {showAction === showActionType.SHOULD_TRANSFER && <TransferForm />}
    </DefaultModal>
  );
}

export default ActionOnEmployeeModal;
